import API_CONFIG from './apiConfig';
import { authFetch } from './authFetch';

export class ProjectSearchService {
  static projectData = null;
  static isLoading = false;
  static loadPromise = null;
  static cacheExpiry = null;
  static CACHE_DURATION = 60 * 60 * 1000; // 1 hour in milliseconds

  // Initialize by loading data from the backend API
  static async initialize() {
    // Check if data is cached and not expired
    if (this.projectData !== null && this.cacheExpiry && Date.now() < this.cacheExpiry) {
      console.log('Using cached project data');
      return this.projectData;
    }
    
    if (this.loadPromise) return this.loadPromise;

    this.isLoading = true;
    console.log('Initializing project search service with backend API data');

    try {
      // Store the promise to prevent multiple loads
      this.loadPromise = this.fetchMilestonesFromAPI();
      const apiData = await this.loadPromise;
      
      // Transform API data to match the expected format for search
      let transformedData = apiData.map(milestone => ({
        'Project Number': milestone.project_number,
        'Project Name': milestone.project_name,
        'Milestone': milestone.milestone_name,
        'PM': milestone.project_manager,
        'Labor': milestone.contract_labor,
        'Pct Labor Used': milestone.forecast_pm_labor
      }));
      
      // Filter out milestones with "Default Milestone" name or "Unassigned" project manager
      let filteredData = transformedData.filter(milestone => 
        milestone['Milestone'] !== 'Default Milestone' && 
        milestone['PM'] !== 'Unassigned'
      );
      
      console.log(`Filtered ${transformedData.length - filteredData.length} milestones (Default Milestone or Unassigned PM)`);
      
      // Sort by milestone name first, then by project name
      this.projectData = filteredData.sort((a, b) => {
        // First sort by milestone name
        const milestoneComparison = (a['Milestone'] || '').localeCompare(b['Milestone'] || '', undefined, { 
          numeric: true, 
          sensitivity: 'base' 
        });
        
        if (milestoneComparison !== 0) {
          return milestoneComparison;
        }
        
        // If milestone names are the same, sort by project name
        return (a['Project Name'] || '').localeCompare(b['Project Name'] || '', undefined, { 
          numeric: true, 
          sensitivity: 'base' 
        });
      });
      
      console.log('Data sorted by milestone name, then project name');
      
      // Set cache expiry time
      this.cacheExpiry = Date.now() + this.CACHE_DURATION;
      
      console.log(`Loaded ${this.projectData.length} project records from API for search`);
      this.isLoading = false;
      this.loadPromise = null;
      return this.projectData;
    } catch (error) {
      console.error('Failed to initialize project search service:', error);
      this.isLoading = false;
      this.loadPromise = null;
      throw error;
    }
  }

  // Fetch milestones data from the backend API
  static async fetchMilestonesFromAPI(retryCount = 0) {
    const maxRetries = 2;
    
    try {
      const response = await authFetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.MILESTONES}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      // Validate the response structure
      if (!Array.isArray(data)) {
        throw new Error('API response is not an array');
      }
      
      console.log(`Fetched ${data.length} milestones from API`);
      return data;
    } catch (error) {
      console.error(`Error fetching milestones from API (attempt ${retryCount + 1}):`, error);
      
      // Retry logic for network errors
      if (retryCount < maxRetries && (
        error.name === 'TypeError' || // Network error
        error.message.includes('Failed to fetch') ||
        error.message.includes('NetworkError')
      )) {
        console.log(`Retrying API call in ${(retryCount + 1) * 1000}ms...`);
        await new Promise(resolve => setTimeout(resolve, (retryCount + 1) * 1000));
        return this.fetchMilestonesFromAPI(retryCount + 1);
      }
      
      throw error;
    }
  }

  // Clear cache (useful for manual refresh or when needed)
  static clearCache() {
    this.projectData = null;
    this.cacheExpiry = null;
    this.loadPromise = null;
    console.log('Project search cache cleared');
  }

  // Get cache status for debugging
  static getCacheStatus() {
    const isDataLoaded = this.projectData !== null;
    const isLoading = this.isLoading;
    const cacheExpired = this.cacheExpiry ? Date.now() >= this.cacheExpiry : true;
    const timeUntilExpiry = this.cacheExpiry ? Math.max(0, this.cacheExpiry - Date.now()) : 0;
    
    return {
      isDataLoaded,
      isLoading,
      cacheExpired,
      timeUntilExpiry: Math.round(timeUntilExpiry / 1000), // in seconds
      recordCount: this.projectData?.length || 0
    };
  }

  // Search for projects that match the search term across project number, name, and milestone
  static async searchProjects(searchTerm) {
    if (!searchTerm || searchTerm.length < 2) {
      return [];
    }

    try {
      // Make sure data is loaded
      if (!this.projectData || (this.cacheExpiry && Date.now() >= this.cacheExpiry)) {
        await this.initialize();
      }

      const searchTermLower = searchTerm.toLowerCase();
      
      // First look for project numbers that start with the search term (highest priority)
      const numberStartsWithMatches = this.projectData.filter(project => 
        project['Project Number'].toLowerCase().startsWith(searchTermLower)
      );
      
      // Then look for project names that start with the search term (high priority)
      const nameStartsWithMatches = this.projectData.filter(project => 
        !project['Project Number'].toLowerCase().startsWith(searchTermLower) &&
        project['Project Name']?.toLowerCase().startsWith(searchTermLower)
      );
      
      // Then look for milestone names that start with the search term (medium priority)
      const milestoneStartsWithMatches = this.projectData.filter(project => 
        !project['Project Number'].toLowerCase().startsWith(searchTermLower) &&
        !project['Project Name']?.toLowerCase().startsWith(searchTermLower) &&
        project['Milestone']?.toLowerCase().startsWith(searchTermLower)
      );
      
      // Then look for partial matches in project number, name, and milestone (lowest priority)
      const partialMatches = this.projectData.filter(project => 
        !project['Project Number'].toLowerCase().startsWith(searchTermLower) &&
        !project['Project Name']?.toLowerCase().startsWith(searchTermLower) &&
        !project['Milestone']?.toLowerCase().startsWith(searchTermLower) &&
        (
          project['Project Number'].toLowerCase().includes(searchTermLower) ||
          project['Project Name']?.toLowerCase().includes(searchTermLower) ||
          project['Milestone']?.toLowerCase().includes(searchTermLower)
        )
      );
      
      // Combine all matches in priority order
      const matches = [
        ...numberStartsWithMatches,
        ...nameStartsWithMatches, 
        ...milestoneStartsWithMatches,
        ...partialMatches
      ];

      console.log(`Found ${matches.length} projects matching "${searchTerm}" from API data`);
      
      // Limit the results to avoid overwhelming the UI
      return matches.slice(0, 40);
    } catch (error) {
      console.error('Error searching projects:', error);
      return [];
    }
  }

  // Get full details for a specific project number (exact match)
  static async getProjectDetails(projectNumber) {
    if (!projectNumber) {
      throw new Error('Project number is required');
    }

    try {
      // Make sure data is loaded
      if (!this.projectData || (this.cacheExpiry && Date.now() >= this.cacheExpiry)) {
        await this.initialize();
      }

      // Find exact match
      const project = this.projectData.find(p => 
        p['Project Number'] === projectNumber
      );

      if (!project) {
        throw new Error(`Project not found: ${projectNumber}`);
      }

      console.log("Raw project data from API:", project);
      console.log("Pct Labor Used value:", project['Pct Labor Used'], "Type:", typeof project['Pct Labor Used']);

      return project;
    } catch (error) {
      console.error('Error getting project details:', error);
      throw error;
    }
  }
  
}

export default ProjectSearchService;